import { useEffect, useCallback, useRef } from 'react';
import usePermissionStore from '../data/permissionStore';
import useTransientStore from '../data/transcientStore';
import { getCurrentUserPermissions } from '../services/UserRoleService';

/**
 * Custom hook to manage and check user permissions
 * This hook provides utilities to load and check function-based permissions
 */
export const usePermissions = () => {
    const currentUserId = useTransientStore((state) => state.currentUserId);
    const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
    const hasRefreshedOnMount = useRef(false);
    
    const {
        roleId,
        roleName,
        addressSystemId,
        functionMap,
        setRolePermissions,
        clearPermissions,
        hasAccess
    } = usePermissionStore();

    /**
     * Load permissions for the current user and address system
     * This should be called when the user selects an address system
     */
    const loadPermissions = useCallback(async () => {
        if (!currentUserId || !selectedAddressSystem || selectedAddressSystem.id <= 0) {
            console.warn('Cannot load permissions: Missing user ID or address system');
            clearPermissions();
            return false;
        }

        try {
            const permissions = await getCurrentUserPermissions(currentUserId, selectedAddressSystem.id);
            
            if (!permissions) {
                console.warn('No permissions returned for user');
                clearPermissions();
                return false;
            }

            // Flatten all functions from all function groups
            const allFunctions = permissions.functionGroups.flatMap(group => group.functions);
            
            setRolePermissions(
                permissions.roleId,
                permissions.roleName,
                permissions.addressSystemId,
                permissions.addressSystemName,
                allFunctions
            );

           // console.log(`Loaded ${allFunctions.length} function permissions for role "${permissions.roleName}"`);
            return true;
        } catch (error) {
            console.error('Error loading permissions:', error);
            clearPermissions();
            return false;
        }
    }, [currentUserId, selectedAddressSystem, setRolePermissions, clearPermissions]);

    /**
     * Check if the current user has access to a specific function
     * @param functionCode The function code to check (e.g., 'CITY_OBJECT_SHOW')
     * @returns true if the user has access, false otherwise
     */
    const checkAccess = useCallback((functionCode: string): boolean => {
        return hasAccess(functionCode);
    }, [hasAccess]);

    /**
     * Check if permissions are loaded for the current address system
     */
    const isLoaded = useCallback((): boolean => {
        return (
            roleId !== null &&
            addressSystemId !== null &&
            addressSystemId === selectedAddressSystem?.id &&
            functionMap.size > 0
        );
    }, [roleId, addressSystemId, selectedAddressSystem, functionMap]);

    /**
     * Automatically refresh permissions on page load/refresh
     * This ensures that any role/permission changes made by an admin are picked up
     * without requiring the user to re-select an address system or log out/in
     */
    useEffect(() => {
        // Only run once on mount when we have valid user and address system
        if (hasRefreshedOnMount.current) {
            return;
        }

        if (currentUserId && selectedAddressSystem && selectedAddressSystem.id > 0) {
            hasRefreshedOnMount.current = true;
            //console.log('Refreshing permissions on page load...');
            loadPermissions();
        }
    }, [currentUserId, selectedAddressSystem, loadPermissions]);

    return {
        // State
        roleId,
        roleName,
        addressSystemId,
        isLoaded: isLoaded(),
        functionCount: functionMap.size,
        
        // Actions
        loadPermissions,
        checkAccess,
        clearPermissions
    };
};

export default usePermissions;
